/* Autosave feedback for the editing grids (audit OE-A21).
 *
 * The access/price matrices save on every click or blur; failures used to go
 * to console.error and nowhere else, so a dropped request looked identical to
 * a saved one. One tracker per grid: saving → saved (with a short undo when
 * the caller supplies an inverse) → idle, or error with retry. Render it with
 * `SaveStatus.svelte`.
 */

export type SaveState = "idle" | "saving" | "saved" | "error";

export class SaveTracker {
  state = $state<SaveState>("idle");
  undo = $state<(() => Promise<void>) | null>(null);
  private lastAction: (() => Promise<void>) | null = null;
  private undoTimer: ReturnType<typeof setTimeout> | null = null;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;

  /** Run a save; `undo` (when given) is offered for ~8 s after success. */
  async run(action: () => Promise<void>, undo?: () => Promise<void>): Promise<boolean> {
    this.state = "saving";
    this.lastAction = action;
    if (this.undoTimer) clearTimeout(this.undoTimer);
    this.undo = null;
    try {
      await action();
      this.state = "saved";
      if (undo) {
        this.undo = undo;
        this.undoTimer = setTimeout(() => (this.undo = null), 8000);
      }
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        if (this.state === "saved") this.state = "idle";
      }, 2500);
      return true;
    } catch (e) {
      console.error("save failed:", e);
      this.state = "error";
      return false;
    }
  }

  async retry(): Promise<void> {
    if (this.lastAction) await this.run(this.lastAction);
  }

  async runUndo(): Promise<void> {
    const u = this.undo;
    this.undo = null;
    if (u) await this.run(u);
  }
}
